// PPV / My Vault panel. Rendered both at /ppv-dashboard (via a
// thin wrapper that adds the sidebar — legacy URL, now redirects
// to /dashboard?tab=vault via App.js) and as a tab inside the
// main /dashboard via Dashboard.js importing PpvDashboard.
// Deliberately does NOT render its own layout chrome so it can
// drop into either host.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = 'https://tribaltech-backend.onrender.com';

const STATUS_COLORS = {
  draft: '#94a3b8', published: '#4ade80', unlisted: '#fbbf24', removed: '#f87171',
};

function dollars(cents) { return '$' + (cents / 100).toFixed(2); }

export default function PpvDashboard() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [connect, setConnect] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [affSettings, setAffSettings] = useState(null);
  const [affBusy, setAffBusy] = useState(false);
  const [storage, setStorage] = useState(null);
  const [leakers, setLeakers] = useState([]);
  const [subSettings, setSubSettings] = useState(null);
  const [subBusy, setSubBusy] = useState(false);
  const [recentSales, setRecentSales] = useState([]);
  const [tipForSale, setTipForSale] = useState(null); // {sale} when modal open

  const token = localStorage.getItem('tribal_token');
  const headers = { Authorization: 'Bearer ' + token };

  async function load() {
    setLoading(true);
    try {
      const [listRes, statusRes, affRes, storageRes, leakRes, subRes, salesRes] = await Promise.all([
        axios.get(API + '/api/ppv/creator/listings', { headers }),
        axios.get(API + '/api/ppv/connect/status', { headers }).catch(() => ({ data: null })),
        axios.get(API + '/api/ppv/creator/affiliate-settings', { headers }).catch(() => ({ data: null })),
        axios.get(API + '/api/ppv/creator/storage-usage', { headers }).catch(() => ({ data: null })),
        axios.get(API + '/api/leaks/creator/known-leakers', { headers }).catch(() => ({ data: { leakers: [] } })),
        axios.get(API + '/api/ppv/creator/subscription-settings', { headers }).catch(() => ({ data: null })),
        axios.get(API + '/api/ppv/creator/recent-sales', { headers }).catch(() => ({ data: { sales: [] } })),
      ]);
      setListings(listRes.data.listings || []);
      setConnect(statusRes.data);
      setAffSettings(affRes.data);
      setStorage(storageRes.data);
      setLeakers(leakRes.data.leakers || []);
      setSubSettings(subRes.data);
      setRecentSales(salesRes.data.sales || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function saveSubscriptionSettings({ enabled, priceCents }) {
    setSubBusy(true);
    try {
      await axios.patch(API + '/api/ppv/creator/subscription-settings', {
        enabled,
        price_cents: priceCents,
      }, { headers });
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
    setSubBusy(false);
  }
  useEffect(() => { load(); }, []);

  async function saveRates(affPct, refPct) {
    setAffBusy(true);
    try {
      await axios.patch(API + '/api/ppv/creator/affiliate-settings', {
        affiliate_commission_percent: affPct,
        referral_commission_percent: refPct,
      }, { headers });
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
    setAffBusy(false);
  }

  async function startConnectOnboarding() {
    setConnectBusy(true);
    try {
      const r = await axios.post(API + '/api/ppv/connect/onboarding-link', {}, { headers });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      alert('Could not start onboarding: ' + (e.response?.data?.error || e.message));
      setConnectBusy(false);
    }
  }

  async function togglePublish(listing) {
    setBusy(listing.id);
    try {
      const path = listing.status === 'published' ? 'unpublish' : 'publish';
      await axios.post(`${API}/api/ppv/listings/${listing.id}/${path}`, {}, { headers });
      await load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    }
    setBusy(null);
  }

  async function deleteListing(listing) {
    const ok = window.confirm(
      `Delete "${listing.title}"?\n\n` +
      `It'll be removed from your dashboard and the public page stops working. ` +
      `Buyers who already purchased keep access through their vault, and the purchase history stays intact.`
    );
    if (!ok) return;
    setBusy(listing.id);
    try {
      await axios.delete(`${API}/api/ppv/listings/${listing.id}`, { headers });
      await load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    }
    setBusy(null);
  }

  const totalListings  = listings.length;
  const published      = listings.filter(l => l.status === 'published').length;
  const totalRevenue   = listings.reduce((s, l) => s + (l.total_revenue_cents || 0), 0);
  const totalSales     = listings.reduce((s, l) => s + (l.total_sales || 0), 0);

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 24 }}>
        <div className="page-title" style={{ marginBottom: 0 }}>My Vault</div>
        <Link to="/ppv-upload" className="btn btn-primary">+ Upload new</Link>
      </div>

        <div style={{ marginBottom: 20, display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap: 16 }}>
          <div className="stat-card">
            <div className="stat-label">Listings</div>
            <div className="stat-value" style={{ color:'var(--purple-light)', fontSize: 22 }}>{totalListings}</div>
            <div className="stat-sub" style={{ fontSize: 11, color:'var(--muted)', marginTop: 4 }}>{published} published</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Subscribers</div>
            <div className="stat-value" style={{ color:'#a78bfa', fontSize: 22 }}>
              {subSettings?.active_subscribers ?? 0}
            </div>
            <div className="stat-sub" style={{ fontSize: 11, color:'var(--muted)', marginTop: 4 }}>
              {subSettings?.subscription_enabled ? 'accepting signups' : 'subscriptions off'}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total sales</div>
            <div className="stat-value" style={{ color:'var(--green)', fontSize: 22 }}>{totalSales}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Revenue earned</div>
            <div className="stat-value" style={{ color:'var(--gold)', fontSize: 22 }}>{dollars(totalRevenue)}</div>
            <div className="stat-sub" style={{ fontSize: 11, color:'var(--muted)', marginTop: 4 }}>
              After 10% platform fee
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Payout status</div>
            {!connect ? (
              <div className="stat-value" style={{ color:'var(--muted)', fontSize: 13 }}>—</div>
            ) : !connect.connected ? (
              <div>
                <div className="stat-value" style={{ color:'var(--coral)', fontSize: 13, marginBottom: 8 }}>Not connected</div>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 11, padding:'4px 10px' }}
                  onClick={startConnectOnboarding}
                  disabled={connectBusy}
                >
                  {connectBusy ? '…' : 'Connect bank'}
                </button>
              </div>
            ) : !connect.charges_enabled ? (
              <div>
                <div className="stat-value" style={{ color:'var(--gold)', fontSize: 13, marginBottom: 8 }}>Incomplete</div>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 11, padding:'4px 10px' }}
                  onClick={startConnectOnboarding}
                  disabled={connectBusy}
                >
                  {connectBusy ? '…' : 'Finish setup'}
                </button>
              </div>
            ) : (
              <div>
                <div className="stat-value" style={{ color:'var(--green)', fontSize: 13 }}>✓ Connected</div>
                <div style={{ fontSize: 10, color:'var(--muted)', marginTop: 4 }}>
                  Payouts {connect.payouts_enabled ? 'enabled' : 'pending'}
                </div>
              </div>
            )}
          </div>
        </div>

        {subSettings && <SubscriptionSettingsPanel data={subSettings} busy={subBusy} onSave={saveSubscriptionSettings} />}
        {leakers.length > 0 && <KnownLeakersPanel leakers={leakers} />}
        {storage && <StorageUsagePanel data={storage} />}
        {recentSales.length > 0 && <RecentSalesPanel sales={recentSales} onTip={setTipForSale} />}

        {tipForSale && (
          <SendTipModal
            sale={tipForSale}
            onClose={() => setTipForSale(null)}
            headers={headers}
          />
        )}

        {affSettings && (
          <AffiliatePanel settings={affSettings} busy={affBusy} onSave={saveRates} />
        )}

        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, margin: 0 }}>Source library</h3>
            <div style={{ fontSize: 12, color:'var(--muted)' }}>
              {listings.length} {listings.length === 1 ? 'video' : 'videos'} · most recent first
            </div>
          </div>

          {loading ? (
            <div style={{ color:'var(--muted)', padding: 20 }}>Loading…</div>
          ) : listings.length === 0 ? (
            <div style={{ color:'var(--muted)', padding: '30px 0', textAlign:'center' }}>
              No listings yet. <Link to="/ppv-upload">Upload your first video</Link>.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Preview</th>
                  <th>Views</th>
                  <th>Sales</th>
                  <th>Revenue</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map(l => (
                  <tr key={l.id}>
                    <td style={{ fontSize: 13 }}>{l.title}</td>
                    <td style={{ fontSize: 12 }}>{dollars(l.price_cents)}</td>
                    <td>
                      <span className="badge" style={{ background: (STATUS_COLORS[l.status] || '#888') + '22', color: STATUS_COLORS[l.status] || '#888' }}>
                        {l.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color:'var(--muted)' }}>
                      {l.file_size_bytes ? fmtBytes(l.file_size_bytes) : '—'}
                    </td>
                    <td style={{ fontSize: 12, color:'var(--muted)' }}>
                      {l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ fontSize: 11, color:'var(--muted)' }}>
                      {l.preview_status === 'ready' ? '✓ ready' : l.preview_status === 'pending' ? 'generating…' : l.preview_status}
                    </td>
                    <td style={{ fontSize: 12 }}>{(l.total_views || 0).toLocaleString()}</td>
                    <td style={{ fontSize: 12 }}>{l.total_sales || 0}</td>
                    <td style={{ fontSize: 12 }}>{dollars(l.total_revenue_cents || 0)}</td>
                    <td>
                      <div style={{ display:'flex', gap: 6 }}>
                        <a
                          className="btn btn-secondary"
                          style={{ padding:'4px 10px', fontSize: 11 }}
                          href={`/ppv/${l.slug || l.id}`}
                          target="_blank"
                          rel="noreferrer"
                        >View</a>
                        <ShareLinkButton listingId={l.slug || l.id} />

                        {l.status !== 'removed' && (
                          <button
                            className={'btn ' + (l.status === 'published' ? 'btn-secondary' : 'btn-primary')}
                            style={{ padding:'4px 10px', fontSize: 11 }}
                            disabled={busy === l.id || l.preview_status !== 'ready'}
                            onClick={() => togglePublish(l)}
                            title={l.preview_status !== 'ready' ? 'Wait for preview to finish' : ''}
                          >
                            {busy === l.id
                              ? '…'
                              : l.status === 'published' ? 'Unpublish' : 'Publish'}
                          </button>
                        )}
                        {l.status !== 'removed' && (
                          <button
                            className="btn"
                            style={{ padding:'4px 10px', fontSize: 11, background:'transparent', border:'1px solid rgba(248,113,113,0.4)', color:'#f87171' }}
                            disabled={busy === l.id}
                            onClick={() => deleteListing(l)}
                            title="Remove listing from your dashboard. Past buyers keep vault access."
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
    </>
  );
}


function AffiliatePanel({ settings, busy, onSave }) {
  const [affPct, setAffPct] = useState(settings.affiliate_commission_percent);
  const [refPct, setRefPct] = useState(settings.referral_commission_percent);
  const [copied, setCopied] = useState(false);

  const referralLink = settings.referral_code
    ? `${window.location.origin}/signup?ref=${settings.referral_code}`
    : '';

  function copyLink() {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 15, marginBottom: 4 }}>Collaborations</h3>
      <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
        Affiliate + creator-referral rates in one place. Drop <code style={{ color:'#a78bfa' }}>?ref=YOUR_CODE</code> on any VaultDirect URL to earn on a peer creator's sale.
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 20, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 6 }}>
            Affiliate commission <span style={{ fontSize: 11 }}>(0-9%, capped below platform fee)</span>
          </div>
          <input
            type="number" min="0" max="9" step="1"
            value={affPct}
            onChange={e => setAffPct(Math.max(0, Math.min(9, parseInt(e.target.value || '0', 10))))}
            style={{ width: 80, padding:'6px 10px', borderRadius: 6, background:'#0a0a0a', border:'1px solid #333', color:'#fff' }}
          />
          <span style={{ marginLeft: 8, color:'var(--muted)', fontSize: 12 }}>%</span>
        </div>
        <div>
          <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 6 }}>
            Creator-referral commission <span style={{ fontSize: 11 }}>(what you'd pay someone who refers you)</span>
          </div>
          <input
            type="number" min="0" max="50" step="1"
            value={refPct}
            onChange={e => setRefPct(Math.max(0, Math.min(50, parseInt(e.target.value || '0', 10))))}
            style={{ width: 80, padding:'6px 10px', borderRadius: 6, background:'#0a0a0a', border:'1px solid #333', color:'#fff' }}
          />
          <span style={{ marginLeft: 8, color:'var(--muted)', fontSize: 12 }}>%</span>
        </div>
      </div>
      <button
        className="btn btn-primary" style={{ fontSize: 12, padding:'6px 14px' }}
        disabled={busy || (affPct === settings.affiliate_commission_percent && refPct === settings.referral_commission_percent)}
        onClick={() => onSave(affPct, refPct)}
      >
        {busy ? 'Saving…' : 'Save rates'}
      </button>

      <div style={{ marginTop: 20, paddingTop: 20, borderTop:'1px solid #1a1a1a' }}>
        <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 6 }}>Your creator-referral code</div>
        {settings.referral_code ? (
          <>
            <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 8 }}>
              <code style={{ background:'#0a0a0a', padding:'8px 14px', borderRadius: 6, border:'1px solid #222',
                             color:'#a78bfa', fontFamily:'monospace', fontSize: 16, letterSpacing: 2 }}>
                {settings.referral_code}
              </code>
              <button onClick={copyLink} style={{ padding:'6px 12px', borderRadius: 6, border:'1px solid #333',
                      background:'transparent', color:'#94a3b8', fontSize: 12, cursor:'pointer' }}>
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
            <div style={{ fontFamily:'monospace', fontSize: 11, color:'var(--muted)', wordBreak:'break-all' }}>
              {referralLink}
            </div>
          </>
        ) : (
          <div style={{ color:'var(--muted)', fontSize: 12 }}>— (refresh to auto-generate)</div>
        )}

        <div style={{ marginTop: 16, display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap: 12 }}>
          <MiniStat label="Creators you've referred" value={settings.referred_creators} />
          <MiniStat label="Total earned" value={'$' + ((settings.referral_earnings_cents || 0) / 100).toFixed(2)} />
          <MiniStat label="Pending payout"   value={'$' + ((settings.referral_pending_cents  || 0) / 100).toFixed(2)} />
        </div>
      </div>
    </div>
  );
}

function RecentSalesPanel({ sales, onTip }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>
          Recent sales
          <span style={{ fontSize: 11, color:'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
            (last 50 purchases — send a thank-you to reward a fan)
          </span>
        </h3>
      </div>
      <table className="table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>When</th>
            <th>Listing</th>
            <th>Buyer</th>
            <th>Amount</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sales.map(s => (
            <tr key={s.id}>
              <td style={{ color:'var(--muted)' }}>
                {s.purchase_timestamp ? new Date(s.purchase_timestamp).toLocaleDateString() : '—'}
              </td>
              <td>{s.listing_title || '—'}</td>
              <td style={{ color:'#cbd5e1' }}>{s.buyer_email}</td>
              <td>${((s.amount_cents || 0) / 100).toFixed(2)}</td>
              <td>
                <span className="badge" style={{ background:'rgba(74,222,128,0.12)', color:'#4ade80' }}>
                  {s.status || '—'}
                </span>
              </td>
              <td>
                <button
                  onClick={() => onTip(s)}
                  style={{ padding:'4px 10px', fontSize: 11, borderRadius: 6, border:'1px solid rgba(167,139,250,0.4)', background:'transparent', color:'#a78bfa', cursor:'pointer' }}
                >
                  Send thank-you
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function SendTipModal({ sale, onClose, headers }) {
  const [amount, setAmount] = React.useState('5.00');
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState('');

  async function send() {
    setBusy(true); setErr('');
    try {
      const cents = Math.round(parseFloat(amount || '0') * 100);
      const r = await axios.post(API + '/api/ppv/creator/tips/send', {
        fan_email: sale.buyer_email,
        amount_cents: cents,
        message: message || null,
      }, { headers });
      setResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
    setBusy(false);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#111', border: '1px solid #222', borderRadius: 12,
        padding: 24, maxWidth: 480, width: '100%',
      }}>
        <h3 style={{ color:'#fff', margin:'0 0 6px', fontSize: 18 }}>Send thank-you</h3>
        <div style={{ color:'var(--muted)', fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
          To <strong style={{ color:'#cbd5e1' }}>{sale.buyer_email}</strong> for their
          purchase of "<em>{sale.listing_title}</em>".
          <br/>
          <span style={{ color:'#fbbf24' }}>Note:</span> Stripe can't mail cash. We'll send a
          personal note + a Stripe Payment Link the fan can <em>optionally</em> tip back —
          platform takes 10%, the rest lands on your Connect payout.
        </div>

        {!result && (
          <>
            <label style={{ fontSize: 12, color:'var(--muted)', display:'block', marginBottom: 6 }}>
              Suggested tip amount (USD)
            </label>
            <input
              type="number" min="1" max="5000" step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={busy}
              style={{ width:'100%', padding: 10, borderRadius: 8, background:'#0a0a0a', border:'1px solid #333', color:'#fff', marginBottom: 12 }}
            />
            <label style={{ fontSize: 12, color:'var(--muted)', display:'block', marginBottom: 6 }}>
              Message (optional)
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={4}
              placeholder="Hey, thanks so much for supporting my work…"
              disabled={busy}
              style={{ width:'100%', padding: 10, borderRadius: 8, background:'#0a0a0a', border:'1px solid #333', color:'#fff', marginBottom: 16, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }}
            />
            {err && <div style={{ color:'#f87171', fontSize: 13, marginBottom: 12 }}>{err}</div>}
            <div style={{ display:'flex', gap: 8, justifyContent:'flex-end' }}>
              <button onClick={onClose} disabled={busy}
                      style={{ padding:'8px 16px', borderRadius: 8, border:'1px solid #333', background:'transparent', color:'#94a3b8', cursor:'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={send} disabled={busy || !amount}
                      style={{ padding:'8px 16px', borderRadius: 8, border:'none', background:'#7c3aed', color:'#fff', cursor:'pointer', fontSize: 13, fontWeight: 600 }}>
                {busy ? 'Sending…' : 'Send email + payment link'}
              </button>
            </div>
          </>
        )}

        {result && (
          <div>
            <div style={{ padding: 12, background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.3)', borderRadius: 8, color:'#4ade80', fontSize: 13, marginBottom: 14 }}>
              ✓ Thank-you sent. Payment link:
              <div style={{ fontFamily:'monospace', fontSize: 11, color:'#a78bfa', marginTop: 6, wordBreak:'break-all' }}>
                {result.payment_link}
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <button onClick={onClose}
                      style={{ padding:'8px 16px', borderRadius: 8, border:'none', background:'#7c3aed', color:'#fff', cursor:'pointer', fontSize: 13, fontWeight: 600 }}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function MiniStat({ label, value }) {
  return (
    <div style={{ background:'#0a0a0a', padding: 12, borderRadius: 8, border:'1px solid #1a1a1a' }}>
      <div style={{ fontSize: 10, color:'var(--muted)', textTransform:'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 16, color:'#fff', marginTop: 4, fontWeight: 600 }}>{value}</div>
    </div>
  );
}


// Builds a share URL pointing at the backend /share/ppv/:id route,
// which serves HTML with Open Graph / Twitter Card meta tags
// (and redirects humans to the real listing page). Copy to the
// clipboard so the creator can paste into Twitter / Discord / etc.
function ShareLinkButton({ listingId }) {
  const [copied, setCopied] = React.useState(false);
  const url = `${API}/api/ppv/${listingId}/og`;
  function copy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  }
  return (
    <button
      className="btn btn-secondary"
      style={{ padding:'4px 10px', fontSize: 11 }}
      onClick={copy}
      title={copied ? 'Copied to clipboard' : 'Share link with Twitter / Discord OG card'}
    >
      {copied ? 'Copied' : 'Share'}
    </button>
  );
}

function SubscriptionSettingsPanel({ data, busy, onSave }) {
  const [price, setPrice] = React.useState(
    data.subscription_price_cents ? (data.subscription_price_cents / 100).toFixed(2) : ''
  );
  const enabled = !!data.subscription_enabled;
  const cents = Math.round(parseFloat(price || '0') * 100);
  const priceDirty = cents !== (data.subscription_price_cents || 0);
  const canEnable = cents >= 500 && (data.has_stripe_price || priceDirty);

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>
          VaultDirect subscription
          <span style={{ fontSize: 11, color:'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
            (monthly fee for full-storefront access)
          </span>
        </h3>
        <div style={{ fontSize: 12, color:'var(--muted)' }}>
          {enabled ? `${data.active_subscribers} active subscriber${data.active_subscribers === 1 ? '' : 's'}` : 'disabled'}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 20, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 6 }}>
            Monthly price (USD, minimum $5)
          </div>
          <div style={{ display:'flex', alignItems:'center', gap: 6 }}>
            <span style={{ color:'var(--muted)', fontSize: 14 }}>$</span>
            <input
              type="number" min="5" step="0.01"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder="9.99"
              style={{ width: 120, padding:'6px 10px', borderRadius: 6, background:'#0a0a0a', border:'1px solid #333', color:'#fff' }}
            />
            <span style={{ color:'var(--muted)', fontSize: 12 }}>/month</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 6 }}>
            Status
          </div>
          <div style={{ fontSize: 13, color: enabled ? '#4ade80' : 'var(--muted)' }}>
            {enabled
              ? '✓ Accepting signups on your storefront'
              : data.has_stripe_price
                ? 'Disabled — flip on to resume signups'
                : 'Set a price and enable to start taking subscribers'}
          </div>
        </div>
      </div>

      <div style={{ display:'flex', gap: 8 }}>
        <button
          className="btn btn-primary" style={{ fontSize: 12, padding:'6px 14px' }}
          disabled={busy || !priceDirty || cents < 500}
          onClick={() => onSave({ priceCents: cents })}
        >
          {busy ? 'Saving…' : (data.has_stripe_price ? 'Update price' : 'Set price')}
        </button>
        {!enabled && canEnable && (
          <button
            className="btn btn-primary" style={{ fontSize: 12, padding:'6px 14px', background:'#4ade80' }}
            disabled={busy}
            onClick={() => onSave({ enabled: true, priceCents: priceDirty ? cents : undefined })}
          >
            {busy ? '…' : 'Enable subscriptions'}
          </button>
        )}
        {enabled && (
          <button
            className="btn btn-secondary" style={{ fontSize: 12, padding:'6px 14px' }}
            disabled={busy}
            onClick={() => onSave({ enabled: false })}
          >
            {busy ? '…' : 'Disable signups'}
          </button>
        )}
      </div>

      {enabled && (
        <div style={{ marginTop: 14, padding: 10, background:'#0a0a0a', borderRadius: 6, fontSize: 12, color:'var(--muted)' }}>
          Existing subscribers keep their current rate even if you change the price.
          Only new signups get billed at the updated amount. Platform takes 10% via
          Stripe Connect; the rest goes straight to your connected bank.
        </div>
      )}
    </div>
  );
}


function KnownLeakersPanel({ leakers }) {
  const serial = leakers.filter(l => l.is_serial_leaker).length;
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>
          Known leakers
          <span style={{ fontSize: 11, color:'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
            (buyers whose watermarked copy was decoded from a leak)
          </span>
        </h3>
        <div style={{ fontSize: 12, color:'var(--muted)' }}>
          {leakers.length} flagged · {serial} cross-creator
        </div>
      </div>
      <table className="table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Buyer (hashed)</th>
            <th>Flags on you</th>
            <th>Listings hit</th>
            <th>Creators affected</th>
            <th>First detected</th>
            <th>Last detected</th>
          </tr>
        </thead>
        <tbody>
          {leakers.map(l => {
            const dates = (l.detection_dates || []).map(d => new Date(d)).sort((a, b) => a - b);
            const first = dates[0];
            const last = dates[dates.length - 1];
            return (
              <tr key={l.hashed_identifier}>
                <td>
                  <code style={{ fontSize: 11, color:'#a78bfa' }}>
                    {l.hashed_identifier.slice(0, 10)}…{l.hashed_identifier.slice(-6)}
                  </code>
                </td>
                <td>{l.flags_on_you}</td>
                <td>{l.affected_listings_on_you}</td>
                <td>
                  {l.is_serial_leaker ? (
                    <span className="badge" style={{ background:'rgba(248,113,113,0.15)', color:'#f87171' }}>
                      {l.creators_affected_total} creators
                    </span>
                  ) : (
                    <span style={{ color:'var(--muted)' }}>only you</span>
                  )}
                </td>
                <td style={{ color:'var(--muted)' }}>{first ? first.toLocaleDateString() : '—'}</td>
                <td style={{ color:'var(--muted)' }}>{last ? last.toLocaleDateString() : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


function fmtBytes(n) {
  if (!n || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i < 2 ? 0 : 2)} ${units[i]}`;
}

function StorageUsagePanel({ data }) {
  const used = data.total_bytes || 0;
  const quota = data.quota_bytes || 0;
  const pct = Math.max(0, Math.min(100, data.percent_used || 0));
  const remaining = Math.max(0, quota - used);

  // Bar colour tracks utilization — purple while comfortable, amber
  // near the cap, red past 90% so the creator notices before they
  // get blocked at upload time.
  const barColor = pct >= 90 ? '#f87171'
                 : pct >= 75 ? '#fbbf24'
                 : '#7c3aed';

  const b = data.buckets || {};
  const breakdown = [
    { key: 'ppv-originals',   label: 'Source videos',   bytes: b['ppv-originals']?.bytes   || 0, files: b['ppv-originals']?.files   || 0 },
    { key: 'ppv-previews',    label: 'Previews',        bytes: b['ppv-previews']?.bytes    || 0, files: b['ppv-previews']?.files    || 0 },
    { key: 'ppv-watermarked', label: 'Delivered copies', bytes: b['ppv-watermarked']?.bytes || 0, files: b['ppv-watermarked']?.files || 0 },
  ];

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Storage</h3>
        <div style={{ fontSize: 12, color:'var(--muted)' }}>
          {fmtBytes(used)} of {fmtBytes(quota)} used · {fmtBytes(remaining)} left
        </div>
      </div>

      <div style={{ height: 10, background:'#0a0a0a', borderRadius: 5, border:'1px solid #1a1a1a', overflow:'hidden' }}>
        <div style={{
          width: pct + '%', height: '100%',
          background: barColor,
          transition: 'width 0.3s ease, background 0.3s ease',
        }} />
      </div>
      <div style={{ fontSize: 11, color:'var(--muted)', marginTop: 6 }}>
        {pct.toFixed(1)}% used · {data.total_files || 0} files across 3 buckets
      </div>

      <div style={{ marginTop: 16, display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap: 10 }}>
        {breakdown.map(row => (
          <div key={row.key} style={{ background:'#0a0a0a', padding: 10, borderRadius: 8, border:'1px solid #1a1a1a' }}>
            <div style={{ fontSize: 10, color:'var(--muted)', textTransform:'uppercase', letterSpacing: 1 }}>
              {row.label}
            </div>
            <div style={{ fontSize: 14, color:'#fff', marginTop: 4, fontWeight: 600 }}>
              {fmtBytes(row.bytes)}
            </div>
            <div style={{ fontSize: 10, color:'var(--muted)', marginTop: 2 }}>
              {row.files} {row.files === 1 ? 'file' : 'files'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
